/* Data-driven docs — every entry renders through DocPage.jsx.
   Keep this file the single source of truth so the Hub search stays in sync. */

export const DOC_CATEGORIES = [
  { slug: "getting-started", name: "Getting started", icon: "Rocket", color: "amber" },
  { slug: "client-portal",   name: "Client portal",   icon: "LayoutDashboard", color: "sky"   },
  { slug: "funding",         name: "Funding",         icon: "TrendingUp", color: "emerald"    },
  { slug: "security",        name: "Security & privacy", icon: "ShieldCheck", color: "slate"  },
  { slug: "admin",           name: "For team members",   icon: "UserCog", color: "violet"    },
];

/* Doc shape:
   { slug, title, subtitle, category, gated, updated, hero, sections: [{heading, body, bullets, tip, callout}], related: [slugs] }
*/
export const DOCS = [
  {
    slug: "client-portal",
    title: "Your client portal",
    subtitle: "The single source of truth for your credit repair and funding journey.",
    category: "client-portal",
    gated: false,
    updated: "2026-07-08",
    hero: { emoji: "🦆", accent: "amber" },
    sections: [
      {
        heading: "What it is",
        body: "The client portal is where every update, document, message, and status change lives. Nothing about your file happens off-platform — if it's not in the portal, it isn't official.",
        bullets: [
          "Live status of your credit repair journey (Pizza Tracker)",
          "Every document you've uploaded or been asked to upload",
          "Direct chat with your specialist team",
          "Your funding readiness score and personalized recommendations",
          "Estimated completion date + next steps",
        ],
      },
      {
        heading: "How to log in",
        body: "Your username is your email. Your temporary password is your LAST NAME + LAST 4 DIGITS OF YOUR PHONE NUMBER (e.g. Smith8899). On first login you'll be required to create a new password before anything else loads.",
        tip: "If you forget your password, use the 'Forgot password' link on the login page — a reset email is sent within seconds.",
      },
      {
        heading: "Where things live",
        bullets: [
          "**Dashboard** — status, pizza tracker, next steps, quick documents",
          "**Messages** — dedicated chat page with your team",
          "**Funding Center** — readiness score + recommended lender programs",
          "**Meetings** — book, reschedule, or cancel calls with a specialist",
          "**Profile** — update your info, upload a photo, change password",
        ],
      },
    ],
    related: ["messages", "documents", "security"],
  },
  {
    slug: "dashboard",
    title: "Reading your dashboard",
    subtitle: "The 4 sections that tell you exactly where you stand.",
    category: "client-portal",
    gated: false,
    updated: "2026-07-08",
    hero: { emoji: "📊", accent: "sky" },
    sections: [
      {
        heading: "The Pizza Tracker",
        body: "Modeled after the Pizza Hut order tracker you already know. It shows the 8 canonical repair stages — Started, Messaged Client, Waiting for Info, Doc Issue, Waiting for Repairs, Waiting on Trade Line, Waiting on Funding, Completed — and highlights the one you're currently on.",
        callout: { tone: "info", text: "If your status hasn't moved in 7+ days, message your specialist. It's usually because we're waiting on documents from you." },
      },
      {
        heading: "Status cards",
        bullets: [
          "**Current status** — the stage you're on right now",
          "**Started on** — the date your file was opened",
          "**Est. completion** — always 30 days from your start date (auto-updated)",
        ],
      },
      {
        heading: "Your team",
        body: "Below the status cards is a card showing every real human on your file, with their photo, title, and online indicator. Green pulse = online now. Gray dot = 'seen 2h ago' style.",
        tip: "Click 'Message' on any team member to open the dedicated chat with them highlighted.",
      },
      {
        heading: "Documents + Requested from you",
        body: "Left column shows what we still need from you. Right column shows everything you've uploaded. Missing items are marked in amber so they're impossible to miss.",
      },
    ],
    related: ["client-portal", "documents", "messages"],
  },
  {
    slug: "documents",
    title: "Uploading documents",
    subtitle: "Every accepted file type, size limit, and how the vault stays secure.",
    category: "client-portal",
    gated: false,
    updated: "2026-07-08",
    hero: { emoji: "📎", accent: "emerald" },
    sections: [
      {
        heading: "How to upload",
        body: "On the dashboard, drag any file into the 'Upload for the team' card, or click it to open your file picker. Uploads happen instantly and your specialist gets a real-time notification.",
      },
      {
        heading: "Accepted files",
        bullets: [
          "PDF, DOC, DOCX, TXT",
          "JPG, PNG, WEBP, HEIC (photo IDs and screenshots)",
          "Max 20 MB per file — larger files should be split or scanned at lower DPI",
        ],
      },
      {
        heading: "Where documents live",
        body: "Files are stored in encrypted object storage. Only you and your assigned team can access them. Preview any file from your dashboard by clicking its filename — no download required.",
        callout: { tone: "success", text: "All files are AES-256 encrypted at rest and transferred over TLS 1.3." },
      },
      {
        heading: "Common documents we ask for",
        bullets: [
          "Photo ID (Driver's License or Passport)",
          "Social Security card",
          "Recent utility bill (proof of address)",
          "Recent pay stub or bank statement",
          "Lease agreement or mortgage statement",
        ],
      },
    ],
    related: ["security", "client-portal"],
  },
  {
    slug: "messages",
    title: "Messaging your team",
    subtitle: "Direct line to Lorenzo, Xzavier, and the rest of your specialists.",
    category: "client-portal",
    gated: false,
    updated: "2026-07-08",
    hero: { emoji: "💬", accent: "amber" },
    sections: [
      {
        heading: "Where to find it",
        body: "Sidebar → **Messages**, or tap 'Message' on any team card on your dashboard. On mobile, the Messages tab is in the bottom nav.",
      },
      {
        heading: "How messaging works",
        body: "Every message is shared with your full team — Lorenzo, Xzavier, and any assigned specialist. This is by design: if the person you'd normally text is off, someone else on the file replies. You never wait on one person.",
      },
      {
        heading: "Real-time everything",
        bullets: [
          "Messages appear instantly via secure server-sent events — no refresh needed",
          "Each bubble shows the sender's photo, name, and title so you always know who's replying",
          "Green pulsing dot next to a team member = they're online right now",
          "Timestamps are always in your local time zone",
        ],
      },
      {
        heading: "Pro tips",
        bullets: [
          "Press **Enter** to send · **Shift+Enter** to add a new line",
          "Screenshot something confusing — pasting an image is often faster than describing it",
          "Ask for a callback in the message and Xzavier will book a Meeting on your behalf",
        ],
        tip: "Response time expectation: within 4 business hours, usually faster during business days.",
      },
    ],
    related: ["client-portal", "meetings"],
  },
  {
    slug: "funding-center",
    title: "The Funding Center",
    subtitle: "Your personalized readiness score, lender programs, and simulator.",
    category: "funding",
    gated: false,
    updated: "2026-07-08",
    hero: { emoji: "🚀", accent: "sky" },
    sections: [
      {
        heading: "Readiness score",
        body: "A 0-100 score based on your current credit profile, utilization, inquiry history, and account age. It updates the moment your file changes. 70+ means most personal cards are within reach. 85+ opens up business credit and higher-limit programs.",
        callout: { tone: "warn", text: "Readiness score is our internal signal — it's not a FICO score, VantageScore, or lender guarantee. It's a preparation index." },
      },
      {
        heading: "Lender programs",
        body: "21 programs across major issuers (Chase, Bank of America, Discover, Navy Federal, Capital One) plus SBA loans, equipment financing, and startup funding. Each program shows the typical qualification criteria, credit score range, estimated limits, and required documents.",
        tip: "Programs you're likely qualified for are highlighted in green. Programs to prepare for are amber. Never-yet-viable are gray.",
      },
      {
        heading: "The simulator",
        body: "Model 'what-if' scenarios: if I pay off this card, if I open a tradeline, if I wait 60 days for the inquiry to age off. The simulator shows the projected readiness score delta so you can plan the sequence.",
      },
    ],
    related: ["client-portal", "how-it-works"],
  },
  {
    slug: "meetings",
    title: "Booking a call",
    subtitle: "How the meeting scheduler works, cancellations, and calendar sync.",
    category: "client-portal",
    gated: false,
    updated: "2026-07-08",
    hero: { emoji: "📅", accent: "violet" },
    sections: [
      {
        heading: "Book a call",
        body: "Sidebar → **Meetings** → 'Book a call'. Pick a reason (Strategy, Doc Review, Funding Prep, Custom), select a time from the calendar, and add optional notes. You'll get a confirmation email with the meeting link.",
      },
      {
        heading: "Reschedule or cancel",
        body: "Every meeting card has 'Reschedule' and 'Cancel' buttons. Cancellations under 4 hours from start time send a real-time notification to your specialist so they're not left waiting.",
      },
      {
        heading: "Types of calls",
        bullets: [
          "**Strategy** (30 min) — plan your next 30-day sprint",
          "**Doc Review** (15 min) — walk through what we need and why",
          "**Funding Prep** (45 min) — pre-application prep before you apply anywhere",
          "**Custom** — anything else, use the notes field to describe it",
        ],
      },
    ],
    related: ["messages", "client-portal"],
  },
  {
    slug: "notifications",
    title: "Notifications & real-time updates",
    subtitle: "How you stay in sync without checking the portal constantly.",
    category: "client-portal",
    gated: false,
    updated: "2026-07-08",
    hero: { emoji: "🔔", accent: "amber" },
    sections: [
      {
        heading: "The notification bell",
        body: "Top-right of every page. A red dot means unread notifications. Click it for the full drop-down of the last 20 events — status changes, new messages, documents received, meetings booked or canceled.",
      },
      {
        heading: "Delivery channels",
        bullets: [
          "**In-app** — always on, instant via server-sent events",
          "**Email** — status changes, welcome messages, meeting confirmations",
          "**SMS** — critical items only (welcome, missed docs, meeting reminders)",
        ],
      },
      {
        heading: "Managing preferences",
        body: "Profile → Notifications → toggle email or SMS individually. Turning off in-app notifications is not supported (the portal wouldn't work without them).",
      },
    ],
    related: ["messages", "client-portal"],
  },
  {
    slug: "security",
    title: "Security & privacy",
    subtitle: "How your data is protected and who has access to it.",
    category: "security",
    gated: false,
    updated: "2026-07-08",
    hero: { emoji: "🔐", accent: "emerald" },
    sections: [
      {
        heading: "Encryption",
        bullets: [
          "**At rest**: AES-256 on every document, message, and profile field containing PII",
          "**In transit**: TLS 1.3 minimum for every request between your browser and the server",
          "**Password storage**: bcrypt with per-user salt — nobody at Credit Quack can see your password, not even the founders",
        ],
      },
      {
        heading: "Who can see what",
        body: "Your assigned team (Lorenzo, Xzavier, plus any credit specialist added to your file) can see your dashboard, documents, and messages. Nobody outside your team — including other Credit Quack clients — can see anything about your file.",
        callout: { tone: "success", text: "We never sell, share, or use your data for marketing to third parties. Full policy at /legal/privacy." },
      },
      {
        heading: "First-login password change",
        body: "Every new account is created with a temporary password (your last name + last 4 of phone) and MUST be changed on first login. The change-password modal cannot be dismissed until a new password is set.",
      },
      {
        heading: "Session security",
        bullets: [
          "Sessions expire after 30 days of inactivity",
          "Password changes invalidate every existing session",
          "Suspicious login attempts trigger email alerts",
        ],
      },
    ],
    related: ["client-portal", "documents"],
  },
];

export const doc = (slug) => DOCS.find((d) => d.slug === slug);
export const docsInCategory = (catSlug) => DOCS.filter((d) => d.category === catSlug);
